import dynamic from "next/dynamic";
import Head from "next/head";
import { useRouter } from "next/router";

import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { jsonLdScriptProps } from "react-schemaorg";

import type { Insight } from "models/insight";
import { twMerge } from "tailwind-merge";
import { useEffectOnce } from "usehooks-ts";

import CustomCard from "components/CustomCard";
import { SKSpinner } from "components/customElements/skSpinner";
import DataSubscriptionsWidget from "components/DataSubscriptions/DataSubscriptionsWidget";
import EntityAnalyticsWidget from "components/EntityAnalyticsWidget";
import GoToTopButton from "components/GoToTopButton";
import InsightLongForm from "components/InsightLongForm";
import { TopNavBarContext } from "components/TopNavBar/TopNavBarContext";
import { useApi } from "hooks/useApi";
import useAuthentication from "hooks/useAuthentication";
import { useGlobalState } from "hooks/useGlobalState";
import useMediaQuery from "hooks/useMediaQuery";
import { ApiEndpoint, baseFetcher } from "utils/baseFetcher";
import { emberAppBaseUrl } from "utils/envVariables";
import { ptSerif, roboto } from "utils/fonts";
import htmlText from "utils/htmlToText";
import { jsonLDForInsight } from "utils/jsonLD";

import styles from "./style.module.scss";

const ProfileCard = dynamic(
  () => import("components/InsightLongForm/ProfileCard")
);

const PremiumServiceWidget = dynamic(
  () => import("components/PremiumServiceWidget")
);

const EntityRecentEvent = dynamic(() => import("../EntityRecentEvent"), {
  ssr: false,
});

const DianomiAd = dynamic(() => import("components/DianomiAd"), {
  ssr: false,
});

const InsightStreamCard = dynamic(() => import("components/InsightStreamCard"));

const InsightRelatedEntities = dynamic(
  () => import("components/InsightRelatedEntities"),
  { ssr: false }
);

const InsightCTAs = dynamic(() => import("./InsightCTAs"));

const topNavBarHeight = 60;
const bottomMargin = 30;

export const getInsightUrl = (slug: string, lang?: string) => {
  const url = new ApiEndpoint(`/api/v2/insights/${slug}`);

  if (lang) {
    url.searchParams.append("locale", lang);
  }

  url.searchParams.append(
    "include",
    [
      "account",
      "account.premium-service-catalogues",
      "company",
      "primary-entity",
      "secondary-entities",
      "content-vertical",
      "locker-items",
      "insight-disclosure",
      "smart-summary",
    ].join(",")
  );

  url.searchParams.append(
    "fields[insights]",
    [
      "aasm-state-updated-at",
      "aasm-state",
      "account",
      "annotations-count",
      "automatic-keywords",
      "boomerang-reason",
      "byline",
      "comments-count",
      "company",
      "content-vertical",
      "conviction",
      "countries",
      "cover-image-url",
      "dislikes-count",
      "embargo-ends-at",
      "executive-summary",
      "format-type",
      "from-search-bot",
      "geographies",
      "gics",
      "imperative",
      "insight-disclosure",
      "introduced-total-views",
      "is-always-embargoed",
      "is-boost",
      "is-broker",
      "is-buyside",
      "is-commissioned",
      "is-embargoed",
      "is-exchange-sponsored",
      "is-issuerpaid",
      "is-noncommercial",
      "is-podcast",
      "is-primer",
      "is-public",
      "is-read",
      "is-skpi",
      "is-syndicated",
      "likes-count",
      "locker-items",
      "my-like-score",
      "no-sector",
      "primary-entity",
      "published-at",
      "published-forked-count",
      "republished-at",
      "restriction-reason",
      "secondary-entities",
      "shareable-links",
      "show-author-names",
      "show-author-names",
      "slug",
      "smart-summary",
      "strategies",
      "tagline",
      "time-horizon",
      "time-to-read",
      "total-account-read",
      "total-actionable",
      "total-combined-read",
      "total-differentiated",
      "total-discussions",
      "total-logical",
      "total-public-read",
      "total-substantive",
      "updated-at",
      "viewer-is-restricted",
    ].join(",")
  );
  url.searchParams.append(
    "fields[entities]",
    "slug,short-name,security,pretty-name,isin,yahoo-ticker"
  );
  url.searchParams.append("fields[content-verticals]", "slug,name");

  return url.href;
};

const InsightPage = ({
  slug: inputSlug,
  insight: partialInsight,
  isModal,
  leftCTA,
  rightCTA,
}: any) => {
  const router = useRouter();
  const slug = inputSlug || (router.query.slug as string);
  const lang = router.query.lang as string;

  const { authenticated, account } = useAuthentication();

  const [signupSource, setSignupSource] = useGlobalState("signupSource");

  const ref = useRef<HTMLDivElement>(null);

  const { setShowBackButton } = useContext(TopNavBarContext);

  const [stikyTop, setStickyTop] = useState(topNavBarHeight);
  const [stikyTopActionBar, setStickyTopActionBar] = useState(topNavBarHeight);

  const media = useMediaQuery();

  const { data: fullInsight, mutate } = useApi(
    getInsightUrl(slug, lang),
    baseFetcher,
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      onSuccess: (data: Insight) => {
        if (data.aasmState === "under_review") {
          location.href = `/insights/${data.slug}?state=under-review`;
        }
        if (data.aasmState === "scheduled") {
          location.href = `/insights/${data.slug}?state=scheduled`;
        }
        setSignupSource({ type: "Insight", id: data.id });
      },
    }
  );

  useEffectOnce(() => {
    if (fullInsight?.aasmState === "under_review") {
      location.href = `/insights/${fullInsight?.slug}?state=under-review`;
    }
    if (fullInsight?.aasmState === "scheduled") {
      location.href = `/insights/${fullInsight?.slug}?state=scheduled`;
    }
  });

  useEffect(() => {
    if (fullInsight && signupSource?.id !== fullInsight?.id) {
      setSignupSource({
        type: "Insight",
        id: fullInsight?.id,
      });
    }
  }, [fullInsight, setSignupSource, signupSource?.id]);

  const insight = fullInsight || partialInsight;

  const {
    imperative,
    tagline,
    byline,
    executiveSummary,
    coverImageUrl,
    publishedAt,

    authorName,

    author,
    entity,
    primaryEntity,
    contentVertical,

    isPublic,
  } = insight || {};

  const imperativeColor = imperative === "bullish" ? "#75ac33" : "#ac334b";

  const title = `${tagline} - ${authorName}`;
  const description = useMemo(
    () => byline || htmlText(executiveSummary),
    [byline, executiveSummary]
  );

  const canonicalUrl = (
    emberAppBaseUrl + (router.asPath === "/" ? "" : router.asPath)
  ).split("?")[0];

  const rightPane = useRef<HTMLDivElement>(null);
  const actionBar = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let updating = false;

    const updateSticky = () => {
      if (!updating) {
        updating = true;

        setTimeout(function () {
          const stickyTop =
            window.innerHeight -
            (rightPane?.current?.clientHeight || 0) -
            bottomMargin;
          setStickyTop(
            stickyTop < topNavBarHeight ? stickyTop : topNavBarHeight
          );

          const stickyTopActionBar =
            window.innerHeight -
            (actionBar?.current?.clientHeight || 0) -
            (isModal ? topNavBarHeight - 10 : 0);
          setStickyTopActionBar(
            stickyTopActionBar < topNavBarHeight + (isModal ? 0 : 10)
              ? stickyTopActionBar + (isModal ? 0 : 10)
              : topNavBarHeight
          );
          updating = false;
        }, 500);
      }
    };

    updateSticky();

    window.addEventListener("scroll", updateSticky, { passive: true });

    return () => {
      window.removeEventListener("scroll", updateSticky);
    };
  }, [
    isModal,
    rightPane?.current?.clientHeight,
    actionBar?.current?.clientHeight,
  ]);

  const insightContent = useMemo(() => {
    if (!insight) {
      return (
        <div className="w-full flex justify-center mt-[15px]">
          <SKSpinner />
        </div>
      );
    }

    const longForm = <InsightLongForm insight={insight} mutate={mutate} />;

    if (media.mobile) {
      return (
        <div className={styles.mobileContainer}>
          <InsightCTAs insight={insight} isMobile mutate={mutate} />
          {longForm}
        </div>
      );
    }

    return (
      <div className={styles.insights_detail_page_container} ref={ref}>
        <div
          className={twMerge(
            roboto.variable,
            ptSerif.variable,
            styles.panPage,
            isModal && styles.modal,
            "print:!p-0"
          )}
        >
          {leftCTA}
          <div className={twMerge(styles.middlePane, "print:!p-0")}>
            {!media.mobile && (
              <div className={twMerge(styles.actionBar, "print:hidden")}>
                {/* have action bar container left with width 64px  */}
              </div>
            )}
            <div className={twMerge(styles.detailPane, "print:!max-w-none")}>
              <CustomCard
                additionalStyle={{
                  borderTop: `${isModal ? 8 : 5}px solid ${imperativeColor}`,
                }}
                className="print:shadow-none print:rounded-none print:px-0"
              >
                {longForm}
              </CustomCard>
            </div>
            <div
              ref={actionBar}
              className={twMerge(styles.actionBar, "print:hidden")}
              style={{ top: stikyTopActionBar }}
            >
              <InsightCTAs insight={insight} mutate={mutate} />
            </div>
          </div>
          {!media.handheld && (
            <div
              ref={rightPane}
              className={twMerge(styles.rightPane, "print:hidden")}
              style={{ top: stikyTop }}
            >
              <DianomiAd type="right_sidebar" />
              {author && <ProfileCard insight={insight} profile={author} />}
              {primaryEntity && (
                <EntityAnalyticsWidget entity={primaryEntity} showEntityStats />
              )}
              {authenticated && author?.hasPremiumServices && (
                <CustomCard className={styles.card}>
                  <PremiumServiceWidget profile={author} isInsightWidget />
                </CustomCard>
              )}
              {account?.isClient && <DataSubscriptionsWidget />}
              {authenticated && entity && (
                <EntityRecentEvent slug={entity.slug} widget />
              )}
              <CustomCard additionalStyle={{ padding: 10 }}>
                <InsightStreamCard insight={insight} />
              </CustomCard>
              <InsightRelatedEntities slug={slug} widget />
            </div>
          )}
          {rightCTA}
        </div>
        <GoToTopButton
          className={twMerge("mr-[30px]", isModal && "text-white")}
          targetRef={ref}
        />
      </div>
    );
  }, [
    account?.isClient,
    authenticated,
    author,
    entity,
    imperativeColor,
    insight,
    isModal,
    leftCTA,
    media.handheld,
    media.mobile,
    mutate,
    primaryEntity,
    rightCTA,
    slug,
    stikyTop,
    stikyTopActionBar,
  ]);

  useEffectOnce(() => {
    if (!media.mobile) return;

    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "instant" });
    }

    if (!authenticated) {
      setShowBackButton(false);
    } else {
      setShowBackButton(true);
    }
  });

  const containsFTSEorMSCI = useMemo(() => {
    console.log([tagline, byline, executiveSummary]);
    return false;

    return [tagline, byline, executiveSummary].some((html) => {
      const text = htmlText(html);
      if (!text) return false;

      return ["FTSE", "MSCI"].some((name) =>
        text
          .toUpperCase()
          .split(/[\s.,;()[\]]/)
          .includes(name)
      );
    });
  }, [byline, executiveSummary, tagline]);

  const shouldIndex = !containsFTSEorMSCI;

  return (
    <div className={styles.insightPageContainer}>
      <Head>
        <title>{title}</title>
        <link rel="canonical" href={canonicalUrl} />
        <meta name="description" content={description} />
        <meta
          name="robots"
          content={
            shouldIndex
              ? "index, follow" + (isPublic ? "" : ", noarchive")
              : "noindex, nofollow"
          }
        />

        {/* Open graph tags for social media etc */}
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={coverImageUrl} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="article" />
        {publishedAt && (
          <meta
            property="article:published_time"
            content={new Date(publishedAt).toISOString()}
          />
        )}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:image" content={coverImageUrl} />
        {insight && (
          <script
            {...jsonLdScriptProps(
              jsonLDForInsight({
                insight,
                entity,
                contentVertical,
                author,
                canonicalUrl,
              }) as any
            )}
          ></script>
        )}
      </Head>
      {insightContent}
    </div>
  );
};

export default InsightPage;
